import React from "react";
import {
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
} from "@altimateai/lego";

interface FilterableHeaderProps {
  children: React.ReactNode;
  options: { value: string; label: string }[];
  selectedValues: string[];
  onSelectionChange: (_values: string[]) => void;
}

export const FilterableHeader: React.FC<FilterableHeaderProps> = ({
  children,
  options,
  selectedValues,
  onSelectionChange,
}) => {
  const hasFilter = selectedValues.length > 0;

  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter(v => v !== value));
    } else {
      onSelectionChange([...selectedValues, value]);
    }
  };

  return (
    <th className="al-p-0 al-text-left al-text-sm al-font-medium">
      <Popover>
        <PopoverTrigger asChild>
          <Stack
            direction="row"
            align="center"
            gap="xs"
            className={`al-p-3 al-cursor-pointer hover:al-bg-gray-200 ${hasFilter ? "al-bg-blue-50" : ""}`}
          >
            {children}
            {hasFilter && (
              <span className="al-text-xs al-bg-blue-500 al-text-white al-rounded-full al-px-1.5 al-min-w-[18px] al-text-center">
                {selectedValues.length}
              </span>
            )}
            <span className="al-text-gray-400 al-text-xs">▼</span>
          </Stack>
        </PopoverTrigger>
        <PopoverContent align="start" className="al-w-48 al-p-2">
          <div className="al-space-y-1">
            <Stack direction="row" justify="between" align="center" className="al-mb-2">
              <span className="al-text-xs al-text-gray-500 al-font-medium">Filter {children}</span>
              {hasFilter && (
                <button
                  onClick={() => onSelectionChange([])}
                  className="al-text-xs al-text-blue-600 hover:al-underline"
                >
                  Clear
                </button>
              )}
            </Stack>
            <div className="al-max-h-48 al-overflow-y-auto al-space-y-1">
              {options.map(option => (
                <Stack
                  key={option.value}
                  direction="row"
                  align="center"
                  gap="sm"
                  className="al-p-1 al-rounded hover:al-bg-gray-100 al-cursor-pointer"
                  onClick={() => toggleValue(option.value)}
                >
                  <Checkbox
                    checked={selectedValues.includes(option.value)}
                    onCheckedChange={() => toggleValue(option.value)}
                  />
                  <span className="al-text-sm">{option.label}</span>
                </Stack>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </th>
  );
};
